// Capa anticorrupción entre el catálogo de permisos de v1 y el vocabulario que ya usan
// `domain/session.rules.ts` y las ~50 comprobaciones de `hasPermission()` repartidas por la app.
//
// v1 sustituyó el árbol anidado por un catálogo plano de 67 claves `recurso:accion`
// (`GET /permissions/catalog`). Traducir aquí evita tocar cada call site y deja las decisiones de
// autorización en un solo archivo revisable. Ver specs/contexts/api-v1-migration/spec.md §4.1.
//
// Cada entrada es `clave heredada → claves de v1 que la satisfacen` (OR).

const RENAMED = {
  // Solo cambia el separador: `_` → `:`.
  clients_manage: ['clients:manage'],
  clients_read_all: ['clients:read_all'],
  clients_read_assigned: ['clients:read_assigned'],
  cloud_manage: ['cloud:manage'],
  cloud_read_all: ['cloud:read_all'],
  catalog_read_all: ['catalog:read'],
  devices_manage: ['devices:manage'],
  devices_read_all: ['devices:read'],
  enterprises_manage: ['enterprises:manage'],
  infrastructure_manage: ['infrastructure:manage'],
  infrastructure_read_all: ['infrastructure:read'],
  roles_manage: ['roles:manage'],
  roles_read_all: ['roles:read'],
  sap_assignment_manage: ['sap-assignment:manage'],
  sla_availability_manage: ['sla_availability:manage'],
  sla_availability_read_all: ['sla_availability:read_all'],
  standby_schedule_manage_all: ['standby_schedule:manage_all'],
  standby_schedule_manage_assigned: ['standby_schedule:manage_assigned'],
  standby_schedule_read: ['standby_schedule:read'],
  standby_schedule_self_manage: ['standby_schedule:self_manage'],
  users_assign: ['users:assign'],
  users_manage: ['users:manage'],
  virtual_inventory_manage: ['virtual_inventory:manage'],

  // Descriptores de catálogo (`domain/descriptors.ts`), que pasan la clave como variable.
  platform_catalog_manage_all: ['platform-catalog:manage'],
  os_catalog_manage_all: ['os-catalog:manage'],
  db_catalog_manage_all: ['database-catalog:manage'],
  sap_catalog_manage_all: ['sap-catalog:manage'],
  subsystem_catalog_manage_all: ['subsystem-catalog:manage'],
} as const;

const SCOPE_COLLAPSED = {
  // v1 dejó de distinguir "todos" de "asignados" en estas acciones. Se acepta la acción única:
  // amplía ligeramente el alcance, nunca lo restringe de más.
  catalog_manage_all: ['catalog:manage'],
  cloud_manage_all: ['cloud:manage'],
  users_read_all: ['users:read'],
  sap_assignment_manage_all: ['sap-assignment:manage'],
  operational_planning_plans_manage: [
    'operational_planning_plans_manage_all',
    'operational_planning_plans_manage_assigned',
  ],
  'operational_planning:manage': [
    'operational_planning_plans_manage_all',
    'operational_planning_plans_manage_assigned',
  ],
} as const;

const RESOURCE_RENAMED = {
  // v1 modela estas capacidades como *asignación* sobre el inventario virtual.
  db_instances_manage: ['database-instance-assignment:manage'],
  sap_instances_manage: ['sap-assignment:manage'],
  subsystem_instances_manage: ['subsystem-assignment:manage', 'virtual_inventory:assign_subsystems'],

  // `deviceInstance.rules.ts` deriva una clave por tipo de equipo; v1 solo tiene `devices:manage`.
  // Se pierde la granularidad por tipo — decisión registrada en improvements.md (G-14).
  devices_manage_server_power: ['devices:manage'],
  devices_manage_server_x86: ['devices:manage'],
  devices_manage_networking: ['devices:manage'],
  devices_manage_security: ['devices:manage'],
  devices_manage_storage: ['devices:manage'],
  devices_manage_infrastructure: ['devices:manage'],
} as const;

const DOMAIN_ABSENT = {
  // v1 no define permisos para backup ni oportunidades. Se adscriben al permiso del dominio que los
  // contiene en la UI en vez de dejar la acción sin restricción: mantener la
  // funcionalidad no debe abrir el acceso. Sustituir en cuanto el backend publique los suyos (G-12).
  backup_policies_manage: ['clients:manage'],
  backup_policies_manage_assigned: ['clients:manage'],
  backup_policies_read_all: ['clients:read_all', 'clients:read_assigned'],

  // Las oportunidades comerciales son trabajo sobre la cuenta del cliente.
  commercial_opportunities_manage_all: ['clients:manage'],
  commercial_opportunities_manage_assigned: ['clients:manage'],
  costs_read: ['clients:read_all', 'clients:read_assigned'],

  // El panel de administración es administración de sistema.
  system_manage: ['system:diagnose'],
} as const;

const LEGACY_TO_V1: Record<string, readonly string[]> = {
  ...RENAMED,
  ...SCOPE_COLLAPSED,
  ...RESOURCE_RENAMED,
  ...DOMAIN_ABSENT,
};

/**
 * Añade al conjunto de permisos de v1 las claves heredadas equivalentes.
 *
 * No elimina las de v1: los pocos call sites que ya preguntan en formato `recurso:accion`
 * (`cloud:manage`, `infrastructure:manage`) siguen funcionando.
 */
export function expandLegacyPermissions(granted: readonly string[]): string[] {
  const effective = new Set(granted);
  for (const [legacyKey, sources] of Object.entries(LEGACY_TO_V1)) {
    if (sources.some((source) => effective.has(source))) effective.add(legacyKey);
  }
  return [...effective];
}

export const LEGACY_PERMISSION_KEYS = Object.keys(LEGACY_TO_V1);
